import type { TableSourceItem } from "../../ai/sourceItems";
import { getFirstFqlTicker, getFqlTickers, getTableCompanyEntities } from "../../ai/tableHelpers";
import { SOURCE_CLIENT_LABELS } from "../colors";

export function formatSourceDate(input: string | undefined): string {
  if (!input) return "";
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return input;
  try {
    return d.toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return input;
  }
}

export const formatSourceCardDate = formatSourceDate;

export function normalizeSourceText(text: string): string {
  return text.replace(/\s+/g, " ").trim().toLowerCase();
}

export function isStreetAccountSource(
  dataSource: string | undefined,
  client: string | undefined
): boolean {
  const text = `${dataSource ?? ""} ${client ?? ""}`.toLowerCase();
  return text.includes("streetaccount") || text.includes("street account");
}

export function getTableDisplayTitle(
  item: TableSourceItem,
  label: string,
  ticker: string | undefined
): string {
  const meta = item.metadata as
    | { companyName?: string; documentName?: string }
    | undefined;
  const explicitTitle = meta?.documentName?.trim() || meta?.companyName?.trim();
  if (explicitTitle) return explicitTitle;
  if (label && label !== "Other") return label;
  if (ticker) return ticker;
  return "FactSet data table";
}

export function shouldShowTableHeaderTitle(
  title: string,
  label: string,
  ticker: string | undefined
): boolean {
  const normalizedTitle = normalizeSourceText(title);
  if (!normalizedTitle) return false;
  if (normalizedTitle === normalizeSourceText(label)) return false;
  if (ticker && normalizedTitle === normalizeSourceText(ticker)) return false;
  return normalizedTitle !== "factset data table";
}

export function getSourceCardTableTitle(
  item: TableSourceItem,
  sourceLabel: string
): string {
  const metadataTitle = item.metadata?.documentName?.trim();
  if (metadataTitle) return metadataTitle;
  const companyName = item.metadata?.companyName?.trim();
  if (companyName) return companyName;
  if (sourceLabel) return sourceLabel;
  return "Data source";
}

export function getTableEntityDisplay(item: TableSourceItem): {
  summary?: string;
  hasMultipleEntities: boolean;
} {
  const companyEntities = getTableCompanyEntities(item.markdown, item.citedUuids);
  const entities = companyEntities.length > 0 ? companyEntities : getFqlTickers(item);
  const fallback = entities.length === 0 ? getFirstFqlTicker(item) : undefined;
  const displayEntities = entities.length > 0 ? entities : fallback ? [fallback] : [];
  const [first, ...rest] = displayEntities;
  return {
    summary: first
      ? rest.length > 0
        ? `${first} + ${rest.length} other${rest.length === 1 ? "" : "s"}`
        : first
      : undefined,
    hasMultipleEntities: rest.length > 0,
  };
}

export function formatSourceLabel(label: string | undefined): string {
  if (!label) return "";
  return SOURCE_CLIENT_LABELS[label] ?? label.trim();
}

